import axios, { AxiosResponse } from 'axios';
import { accessToken } from './agent';
import { creatorApiBaseURL } from './creatorApiConfig';
import { GENERIC_ERROR_MESSAGE } from '../../utils/resolveErrorMessage';
import type { TApiEnvelope, TEmptyApiEnvelope } from '../models/CreatorApiModels';

/**
 * The creator API's shared request plumbing: one axios instance carrying the bearer token, plus
 * envelope unwrapping. The agents that talk to this API — `aiSessionAgent`, `aiMediaAgent` — build
 * their endpoint groups on the `requests` exported here, the way `synthesiaAgent` builds on the
 * instance `lmsIntegrationAgent` exports. It owns no endpoints of its own, so it has no default export.
 *
 * Knows nothing about SignalR, the stores or React. Everything here resolves to unwrapped envelope
 * data or rejects with the envelope's own error message.
 */

/**
 * A refusal the backend wrote itself: its message is the envelope's own words, and its status is the
 * one the backend answered with.
 *
 * Being this type is the promise. Only a failure carrying an envelope becomes one, so a caller may
 * show its message to an author verbatim without checking anything further; a transport that never
 * reached an envelope rejects as whatever it was, and its message is for a log. The status is for an
 * endpoint that answers more than one way — two refusals can both be failures and still mean
 * different things — and is undefined when the envelope did not arrive over HTTP.
 */
export class CreatorApiError extends Error {
  readonly status: number | undefined;

  constructor(message: string, status?: number) {
    super(message);
    this.name = 'CreatorApiError';
    this.status = status;
  }
}

export const axiosCreatorApi = axios.create({
  baseURL: creatorApiBaseURL,
});

axiosCreatorApi.interceptors.request.use(function (config) {
  if (accessToken) {
    config.headers.authorization = `Bearer ${accessToken}`;
  } else if (config.headers && 'authorization' in config.headers) {
    delete config.headers.authorization;
  }
  return config;
});

// Error envelopes can also arrive as HTTP 400/500; surface their errorMessage.
axiosCreatorApi.interceptors.response.use(
  (response) => response,
  (error) => {
    const envelopeErrorMessage = error?.response?.data?.errorMessage;
    if (typeof envelopeErrorMessage !== 'string' || !envelopeErrorMessage) return Promise.reject(error);

    return Promise.reject(new CreatorApiError(envelopeErrorMessage, error?.response?.status));
  },
);

const responseBody = <T>(response: AxiosResponse<T>) => response.data;

// A failure envelope can also arrive over HTTP 200, and it is no less the backend's own words for it.
const unwrapEnvelope = <T>(envelope: TApiEnvelope<T>): T | null => {
  if (!envelope || envelope.success !== true) {
    throw new CreatorApiError(envelope?.errorMessage || GENERIC_ERROR_MESSAGE);
  }
  return envelope.data;
};

const unwrapEmptyEnvelope = (envelope: TEmptyApiEnvelope): void => {
  if (!envelope || envelope.success !== true) {
    throw new CreatorApiError(envelope?.errorMessage || GENERIC_ERROR_MESSAGE);
  }
};

export const requests = {
  postEnvelope: <T>(url: string, body?: unknown) => axiosCreatorApi.post<TApiEnvelope<T>>(url, body).then(responseBody).then(unwrapEnvelope),
  postEmptyEnvelope: (url: string, body?: unknown) => axiosCreatorApi.post<TEmptyApiEnvelope>(url, body).then(responseBody).then(unwrapEmptyEnvelope),
};

export const requireData = <T>(data: T | null): T => {
  if (data === null || data === undefined) {
    throw new Error(GENERIC_ERROR_MESSAGE);
  }
  return data;
};
